import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateGST, formatCurrency } from '../utils/calculations';
import { generateGSTPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const gstFaqs = [
  {
    question: 'Can this GST calculator add and remove GST from an amount?',
    answer: 'Yes. You can use exclusive mode to add GST on a base amount, or inclusive mode to remove GST from a total amount.',
  },
  {
    question: 'Does this calculator show CGST and SGST separately?',
    answer: 'Yes. For intra-state transactions, the tool splits total GST equally into CGST and SGST for easier invoice understanding.',
  },
  {
    question: 'When does IGST apply instead of CGST and SGST?',
    answer: 'IGST generally applies on inter-state transactions, while CGST and SGST apply on intra-state transactions.',
  },
  {
    question: 'Can I use a custom GST rate?',
    answer: 'Yes. Along with preset buttons like 5%, 12%, 18%, and 28%, you can manually enter a custom GST percentage.',
  },
];

const GSTCalculator = () => {
  const [amount, setAmount] = useState(10000);
  const [gstRate, setGstRate] = useState(18);
  const [isInclusive, setIsInclusive] = useState(false);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [amount, gstRate, isInclusive]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const a = Number(amount) || 0;
    const r = Number(gstRate) || 0;
    const gstResult = calculateGST(a, r, isInclusive);
    setResult(gstResult);
  };

  const handleDownloadPDF = () => {
    generateGSTPDF(result);
  };

  const handleReset = () => {
    setAmount(10000);
    setGstRate(18);
    setIsInclusive(false);
  };

  const gstRates = [0, 5, 12, 18, 28];

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="GST Calculator"
        description="Free GST Calculator India - Calculate CGST, SGST, IGST. Add or remove GST from price. All GST rates: 5%, 12%, 18%, 28%."
        keywords="GST calculator, GST calculator India, CGST SGST calculator, IGST calculator, GST rate calculator, add GST, remove GST"
        canonicalPath="/gst"
        faqItems={gstFaqs}
      />
      <div className="calculator-header">
        <h1>GST Calculator</h1>
        <p>Calculate GST for goods and services in India</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Calculation Type</label>
            <div className="flex-row" style={{ marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={!isInclusive}
                  onChange={() => setIsInclusive(false)}
                />
                Add GST (Exclusive)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={isInclusive}
                  onChange={() => setIsInclusive(true)}
                />
                Remove GST (Inclusive)
              </label>
            </div>
          </div>

          <div className="input-group">
            <label>{isInclusive ? 'Total Amount (with GST)' : 'Base Amount'} (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={amount === '' ? '' : amount}
                onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="input-group">
            <label>GST Rate (%)</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              {gstRates.map((rate) => (
                <button
                  key={rate}
                  className={`btn ${gstRate === rate ? 'btn-primary' : 'btn-outline'}`}
                  style={{ padding: '8px 16px', minWidth: '60px' }}
                  onClick={() => setGstRate(rate)}
                >
                  {rate}%
                </button>
              ))}
            </div>
            <div style={{ marginTop: '12px' }}>
              <label>Or enter custom rate:</label>
              <input
                type="number"
                value={gstRate === '' ? '' : gstRate}
                onChange={(e) => setGstRate(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="100"
                step="0.1"
                style={{ marginTop: '8px' }}
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>{isInclusive ? 'Base Amount (Excl. GST)' : 'Total Amount (Incl. GST)'}</h3>
                <div className="amount">
                  {formatCurrency(isInclusive ? result.baseAmount : result.totalAmount)}
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Base Amount</span>
                  <span className="value">{formatCurrency(result.baseAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">CGST @ {gstRate / 2}%</span>
                  <span className="value">{formatCurrency(result.cgst)}</span>
                </div>
                <div className="result-item">
                  <span className="label">SGST @ {gstRate / 2}%</span>
                  <span className="value">{formatCurrency(result.sgst)}</span>
                </div>
                <div className="result-item" style={{ background: '#dbeafe' }}>
                  <span className="label">Total GST @ {gstRate}%</span>
                  <span className="value" style={{ color: '#2563eb', fontWeight: 'bold' }}>
                    {formatCurrency(result.gstAmount)}
                  </span>
                </div>
                <div className="result-item" style={{ background: '#dcfce7' }}>
                  <span className="label">Total Amount</span>
                  <span className="value" style={{ color: '#059669', fontWeight: 'bold' }}>
                    {formatCurrency(result.totalAmount)}
                  </span>
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '20px' }}>
                <h4>Invoice Breakdown</h4>
                <table className="schedule-table">
                  <tbody>
                    <tr>
                      <td>Taxable Value</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(result.baseAmount)}</td>
                    </tr>
                    <tr>
                      <td>CGST ({gstRate / 2}%)</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(result.cgst)}</td>
                    </tr>
                    <tr>
                      <td>SGST ({gstRate / 2}%)</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(result.sgst)}</td>
                    </tr>
                    <tr style={{ fontWeight: 'bold', borderTop: '2px solid var(--border-color)' }}>
                      <td>Invoice Total</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(result.totalAmount)}</td>
                    </tr>
                  </tbody>
                </table>
                <small style={{ display: 'block', marginTop: '8px', color: 'var(--text-secondary)' }}>
                  * For inter-state transactions, IGST @ {gstRate}% applies instead of CGST + SGST
                </small>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>GST Rate Categories</h4>
        <ul>
          <li><strong>0%:</strong> Essential items like milk, vegetables, grains</li>
          <li><strong>5%:</strong> Mass consumption items like sugar, tea, edible oil</li>
          <li><strong>12%:</strong> Standard goods like processed food, computers</li>
          <li><strong>18%:</strong> Most services and goods like electronics, restaurants</li>
          <li><strong>28%:</strong> Luxury items like cars, air conditioners, tobacco</li>
        </ul>
        <p style={{ marginTop: '8px' }}>
          <strong>Note:</strong> CGST (Central) + SGST (State) for intra-state, IGST for inter-state transactions.
        </p>
      </div>

      <section className="info-box" style={{ marginTop: '24px' }}>
        <h4>GST Calculator FAQs</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          {gstFaqs.map((item) => (
            <details key={item.question} style={{ border: '1px solid #dbe3f0', borderRadius: 12, background: '#fff', padding: '14px 16px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#0f172a' }}>{item.question}</summary>
              <p style={{ margin: '12px 0 0', color: '#475569', lineHeight: 1.65 }}>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

export default GSTCalculator;
